import { useEffect, useRef, useState } from "react";
import { Button } from "react-aria-components";
import { useLocation, useNavigate } from "react-router";

import "@/features/experience-strategy/experienceStrategyConversation.css";

import type { ExperienceStrategyProposalAdjustmentReference } from "@/features/experience-strategy/experienceStrategyAdjustment";
import {
  useConfirmExperienceStrategyProposalMutation,
  useGetExperienceStrategyProposalQuery,
  useRejectExperienceStrategyProposalMutation,
} from "@/features/experience-strategy/experienceStrategyApi";
import {
  experienceStrategyDimensionLabels,
  type ExperienceStrategyProposalPart,
} from "@/features/experience-strategy/experienceStrategySchemas";
import {
  experienceStrategyOverviewPath,
  safeExperienceStrategyReturnTo,
} from "@/features/experience-strategy/experienceStrategyQuery";

interface ExperienceStrategyProposalCardProps {
  immutablePart: ExperienceStrategyProposalPart;
  onAdjust: (reference: ExperienceStrategyProposalAdjustmentReference) => void;
  organizationId: string;
  shopBindingId: string;
}

export function ExperienceStrategyProposalCard({
  immutablePart,
  onAdjust,
  organizationId,
  shopBindingId,
}: ExperienceStrategyProposalCardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const snapshot = immutablePart.data;
  const [pollingInterval, setPollingInterval] = useState(5_000);
  const proposalQuery = useGetExperienceStrategyProposalQuery(
    { organizationId, proposalId: snapshot.proposal_id },
    {
      pollingInterval,
      refetchOnMountOrArgChange: true,
      skipPollingIfUnfocused: true,
    },
  );
  const [confirm, confirmState] =
    useConfirmExperienceStrategyProposalMutation();
  const [reject, rejectState] = useRejectExperienceStrategyProposalMutation();
  const idempotencyKey = useRef(`experience-strategy-${crypto.randomUUID()}`);
  const live = proposalQuery.data?.proposal;
  const liveBelongsToSnapshot = Boolean(
    live &&
    live.id === snapshot.proposal_id &&
    live.organization_id === organizationId &&
    live.scope_id === snapshot.scope_id,
  );
  const proposal = liveBelongsToSnapshot ? live : null;
  const draft = proposal?.draft ?? snapshot.draft;
  const pending = confirmState.isLoading || rejectState.isLoading;
  const localCompleted = confirmState.isSuccess || rejectState.isSuccess;
  const canAct =
    proposal?.status === "awaiting_confirmation" && !pending && !localCompleted;
  const mutationError = confirmState.error || rejectState.error;
  const returnTo = strategyReturnTo(location.state, shopBindingId);
  const overviewPath = experienceStrategyOverviewPath(shopBindingId);

  useEffect(() => {
    const terminal = proposal
      ? ["confirmed", "rejected", "stale"].includes(proposal.status)
      : proposalQuery.isError;
    const frame = window.requestAnimationFrame(() => {
      setPollingInterval(terminal ? 0 : 5_000);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [proposal, proposalQuery.isError]);

  const confirmProposal = async () => {
    if (!proposal || !canAct) return;
    try {
      await confirm({
        action: proposal.action,
        baseVersionId: proposal.base_version_id,
        expectedScopeRevision: proposal.expected_scope_revision,
        expectedStrategyRevision: proposal.expected_strategy_revision,
        idempotencyKey: idempotencyKey.current,
        organizationId,
        proposalHash: proposal.proposal_hash,
        proposalId: proposal.id,
        proposalRevision: proposal.revision,
        strategyId: proposal.strategy_id,
      }).unwrap();
      if (returnTo) navigate(returnTo, { replace: true });
    } catch (error) {
      if (isProposalConflict(error)) await proposalQuery.refetch();
      // mutation 状态在卡片内统一渲染，避免事件 Promise 形成未处理拒绝。
    }
  };

  const rejectProposal = async () => {
    if (!proposal || !canAct) return;
    try {
      await reject({
        expectedRevision: proposal.revision,
        organizationId,
        proposalHash: proposal.proposal_hash,
        proposalId: proposal.id,
      }).unwrap();
    } catch (error) {
      if (isProposalConflict(error)) await proposalQuery.refetch();
      // mutation 状态在卡片内统一渲染，避免事件 Promise 形成未处理拒绝。
    }
  };

  const adjustProposal = () => {
    if (!proposal || !canAct) return;
    onAdjust({
      proposal_hash: proposal.proposal_hash,
      proposal_id: proposal.id,
      proposal_revision: proposal.revision,
      schema: "experience_strategy_proposal_adjustment_ref.v1",
    });
  };

  return (
    <section
      aria-label="经验策略确认"
      className="experience-strategy-proposal"
      data-status={proposal?.status ?? "hydrating"}
      data-testid={`experience-strategy-proposal-${snapshot.proposal_id}`}
    >
      <header>
        <span>{proposalActionLabel(proposal?.action ?? snapshot.action)}</span>
        <strong>{draft.title}</strong>
      </header>
      {draft.summary ? <p>{draft.summary}</p> : null}
      <div className="experience-strategy-proposal__dimensions">
        {draft.dimensions.map((dimension) => (
          <article key={dimension.key}>
            <span>{experienceStrategyDimensionLabels[dimension.key]}</span>
            <strong>{dimension.headline}</strong>
            {dimension.summary ? <p>{dimension.summary}</p> : null}
          </article>
        ))}
      </div>

      {confirmState.isSuccess ? (
        <div className="experience-strategy-proposal__notice" role="status">
          <span>
            {proposal?.action === "adjust"
              ? "策略调整已经确认并生效。"
              : "这份策略已经确认并生效。"}
          </span>
          <Button onPress={() => navigate(overviewPath)}>查看经验策略</Button>
        </div>
      ) : rejectState.isSuccess ? (
        <p className="experience-strategy-proposal__notice" role="status">
          这份策略已被拒绝。
        </p>
      ) : proposalQuery.isError || (live && !liveBelongsToSnapshot) ? (
        <div className="experience-strategy-proposal__notice" role="alert">
          <span>方案状态暂时无法核对，已禁用确认操作。</span>
          <Button onPress={() => void proposalQuery.refetch()}>重新核对</Button>
        </div>
      ) : proposalQuery.isLoading && !proposal ? (
        <p className="experience-strategy-proposal__notice" role="status">
          正在核对方案状态…
        </p>
      ) : proposal && proposal.status !== "awaiting_confirmation" ? (
        <p className="experience-strategy-proposal__notice" role="status">
          {proposalStatusLabel(proposal.status)}
        </p>
      ) : (
        <div className="experience-strategy-proposal__actions">
          <Button isDisabled={!canAct} onPress={() => void confirmProposal()}>
            {confirmState.isLoading
              ? "正在确认…"
              : proposal?.action === "adjust"
                ? "确认调整"
                : "确认策略"}
          </Button>
          <Button isDisabled={!canAct} onPress={adjustProposal}>
            调整方案
          </Button>
          <Button isDisabled={!canAct} onPress={() => void rejectProposal()}>
            {rejectState.isLoading ? "正在拒绝…" : "拒绝"}
          </Button>
        </div>
      )}

      {mutationError ? (
        <p className="experience-strategy-proposal__error" role="alert">
          {proposalMutationErrorLabel(mutationError)}
        </p>
      ) : null}
    </section>
  );
}

function strategyReturnTo(value: unknown, shopBindingId: string) {
  if (!value || typeof value !== "object") return "";
  const candidate = value as Record<string, unknown>;
  const safeReturnTo = safeExperienceStrategyReturnTo(candidate.strategyReturnTo);
  return safeReturnTo === experienceStrategyOverviewPath(shopBindingId)
    ? safeReturnTo
    : "";
}

function proposalActionLabel(action: "adjust" | "confirm") {
  return action === "adjust" ? "策略调整方案" : "经验策略方案";
}

function proposalStatusLabel(status: string) {
  switch (status) {
    case "confirmed":
      return "这份策略已经确认并生效。";
    case "rejected":
      return "这份策略已被拒绝。";
    case "stale":
      return "这份策略已失效，请让 Merry 重新生成。";
    case "drafted":
      return "方案正在准备，暂时不能确认。";
    default:
      return "方案状态已更新。";
  }
}

function proposalMutationErrorLabel(error: unknown) {
  if (isProposalConflict(error)) {
    return "方案已经发生变化，请重新核对最新状态后再操作。";
  }
  return "操作未完成，请核对网络和方案状态后重试。";
}

function isProposalConflict(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    error.status === 409
  );
}
